<template>
  <section class="statement-section">
    <div class="statement-section__header">
      <div class="statement-section__marker">7</div>
      <div>
        <h3 class="statement-section__title">Документы</h3>
        <p class="statement-section__description">
          Отметьте документы, принятые от абитуриента
        </p>
      </div>
    </div>

    <v-table v-if="isStaff" class="statement-documents-table">
      <thead>
        <tr>
          <th class="statement-documents-table__mark"></th>
          <th>Документ</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="document in documents"
          :key="document.id"
          :class="{ 'statement-documents-table__row--selected': isSelected(document.id) }"
          class="statement-documents-table__row"
          @click="toggleDocument(document.id)"
        >
          <td class="statement-documents-table__mark">
            <v-icon v-if="isSelected(document.id)" color="success" icon="mdi-check-circle" size="20" />
          </td>
          <td class="statement-documents-table__document">
            <div class="statement-documents-table__name">
              {{ document.name }}
            </div>
            <v-text-field autocomplete="one-time-code"
              v-if="isSelected(document.id)"
              class="statement-documents-table__comment"
              :model-value="getAdditionalText(document.id)"
              bg-color="white"
              density="compact"
              variant="outlined"
              hide-details
              maxlength="250"
              placeholder="Комментарий"
              @update:model-value="value => updateAdditionalText(document.id, value)"
              @click.stop
            />
          </td>
        </tr>
      </tbody>
    </v-table>

    <div v-if="isStaff && receiptUrl" class="statement-documents-table__receipt">
      <a :href="receiptUrl" target="_blank" rel="noopener">
        Печать расписки
      </a>
    </div>

    <div v-if="scanDocuments.length" class="statement-document-scans">
      <h4 class="statement-document-scans__title">Загрузить скан документа:</h4>
      <p class="statement-document-scans__hint">
        Максимальный размер - {{ uploadMaxSize }} МБ; {{ availableUploadExtensions }}
      </p>

      <div class="statement-document-scans__buttons">
        <div
          v-for="document in scanDocuments"
          :key="document.id"
          class="statement-document-scans__item"
        >
          <input
            :ref="`scanInput${document.id}`"
            class="statement-document-scans__input"
            type="file"
            :accept="acceptExtensions"
            @change="event => uploadDocumentScan(document, event)"
          >
          <v-btn
            class="statement-document-scans__upload-button"
            color="primary"
            variant="tonal"
            prepend-icon="mdi-upload"
            :loading="uploadingDocumentId === String(document.id)"
            :disabled="!applicationId || uploadingDocumentId !== null"
            @click="openScanInput(document.id)"
          >
            {{ document.name }}
          </v-btn>
        </div>
      </div>

      <div v-if="uploadedScans.length" class="statement-document-scans__list">
        <div
          v-for="scan in uploadedScans"
          :key="scan.id"
          class="statement-document-scans__row"
        >
          <div class="statement-document-scans__file">
            <span class="statement-document-scans__doctype">{{ scan.name }}</span>
            <span class="statement-document-scans__filename">{{ scan.filename }}</span>
          </div>
          <div class="statement-document-scans__actions">
            <v-btn
              color="primary"
              variant="text"
              size="small"
              prepend-icon="mdi-download"
              :loading="downloadingScanId === String(scan.id)"
              :disabled="uploadingDocumentId !== null || deletingScanId !== null"
              @click="downloadDocumentScan(scan)"
            >
              Скачать
            </v-btn>
            <v-btn
              color="error"
              variant="text"
              size="small"
              prepend-icon="mdi-delete"
              :loading="deletingScanId === String(scan.id)"
              :disabled="uploadingDocumentId !== null || downloadingScanId !== null || (!isStaff && formData.approved)"
              @click="deleteDocumentScan(scan)"
            >
              Удалить
            </v-btn>
          </div>
        </div>
      </div>

      <div v-if="uploadMessage" class="statement-document-scans__message">
        {{ uploadMessage }}
      </div>
      <div v-if="uploadError" class="statement-document-scans__error">
        {{ uploadError }}
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'DocumentsSection',
  props: {
    formData: {
      type: Object,
      required: true
    },
    applicationId: {
      type: [Number, String],
      default: ''
    },
    editPassword: {
      type: String,
      default: ''
    },
    isStaff: {
      type: Boolean,
      default: false
    },
    documents: {
      type: Array,
      default: () => []
    },
    scanDocuments: {
      type: Array,
      default: () => []
    },
    uploadMaxSize: {
      type: [Number, String],
      default: ''
    },
    availableUploadExtensions: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      uploadingDocumentId: null,
      downloadingScanId: null,
      deletingScanId: null,
      uploadError: '',
      uploadMessage: ''
    }
  },
  computed: {
    uploadedScans() {
      return Array.isArray(this.formData.documentsScan)
        ? this.formData.documentsScan
        : []
    },
    acceptExtensions() {
      return String(this.availableUploadExtensions || '')
        .split(',')
        .map(extension => extension.trim())
        .filter(Boolean)
        .map(extension => `.${extension}`)
        .join(',')
    },
    receiptUrl() {
      if (!this.formData.pa_id) {
        return ''
      }

      return `${this.$model.wwwroot}/blocks/manage_groups/admission_new/print_receipt.php?user_id=${this.formData.pa_id}`
    }
  },
  methods: {
    getDocumentKey(documentId) {
      return String(documentId)
    },
    isSelected(documentId) {
      return Boolean(this.formData.acceptedDocuments[this.getDocumentKey(documentId)])
    },
    toggleDocument(documentId) {
      const key = this.getDocumentKey(documentId)

      if (this.isSelected(documentId)) {
        delete this.formData.acceptedDocuments[key]
        return
      }

      this.formData.acceptedDocuments[key] = {
        additionalText: ''
      }
    },
    getAdditionalText(documentId) {
      const document = this.formData.acceptedDocuments[this.getDocumentKey(documentId)]

      return document ? document.additionalText : ''
    },
    updateAdditionalText(documentId, value) {
      const key = this.getDocumentKey(documentId)

      if (!this.formData.acceptedDocuments[key]) {
        return
      }

      this.formData.acceptedDocuments[key].additionalText = value
    },
    openScanInput(documentId) {
      const input = this.$refs[`scanInput${documentId}`]
      const element = Array.isArray(input) ? input[0] : input

      if (element) {
        element.click()
      }
    },
    uploadDocumentScan(document, event) {
      const file = event.target.files && event.target.files[0]

      if (!file) {
        return
      }

      this.uploadingDocumentId = String(document.id)
      this.uploadError = ''
      this.uploadMessage = ''

      const requestData = new FormData()
      requestData.append('id', this.applicationId)
      requestData.append('documentId', document.id)
      if (!this.isStaff && this.editPassword) {
        requestData.append('password', this.editPassword)
      }
      requestData.append('scan', file)

      this.axios
        .post(
          this.$model.wwwroot +
          "/procollege/manage_groups/admission/createApplicant/uploadDocumentScan",
          requestData
        )
        .then((response) => {
          const data = response.data || {}
          const errors = Array.isArray(data.errors) ? data.errors : []

          if (errors.length) {
            this.uploadError = errors.join('; ')
            return
          }

          if (data.scan) {
            this.formData.documentsScan = [
              ...this.uploadedScans,
              data.scan
            ]
          }

          this.uploadMessage = `Файл "${file.name}" загружен`
        })
        .catch((error) => {
          const data = error.response && error.response.data
            ? error.response.data
            : {}

          this.uploadError = Array.isArray(data.errors)
            ? data.errors.join('; ')
            : 'Не удалось загрузить файл'
        })
        .finally(() => {
          this.uploadingDocumentId = null
          event.target.value = ''
        })
    },
    deleteDocumentScan(scan) {
      if (!scan || !scan.id || this.deletingScanId) {
        return
      }

      this.deletingScanId = String(scan.id)
      this.uploadError = ''
      this.uploadMessage = ''

      const requestData = new FormData()
      requestData.append('id', this.applicationId)
      requestData.append('scanId', scan.id)
      if (!this.isStaff && this.editPassword) {
        requestData.append('password', this.editPassword)
      }

      this.axios
        .post(
          this.$model.wwwroot +
          "/procollege/manage_groups/admission/createApplicant/deleteDocumentScan",
          requestData
        )
        .then((response) => {
          const data = response.data

          if (data === false || (data && Array.isArray(data.errors) && data.errors.length)) {
            this.uploadError = data && Array.isArray(data.errors)
              ? data.errors.join('; ')
              : 'Не удалось удалить файл'
            return
          }

          this.formData.documentsScan = this.uploadedScans.filter(
            item => String(item.id) !== String(scan.id)
          )
          this.uploadMessage = `Файл "${scan.filename}" удален`
        })
        .catch((error) => {
          const data = error.response && error.response.data
            ? error.response.data
            : {}

          this.uploadError = Array.isArray(data.errors)
            ? data.errors.join('; ')
            : 'Не удалось удалить файл'
        })
        .finally(() => {
          this.deletingScanId = null
        })
    },
    downloadDocumentScan(scan) {
      if (!scan || !scan.id || this.downloadingScanId) {
        return
      }

      this.downloadingScanId = String(scan.id)
      this.uploadError = ''

      const requestData = new FormData()
      requestData.append('id', scan.id)
      requestData.append('applicationId', this.applicationId)
      if (!this.isStaff && this.editPassword) {
        requestData.append('password', this.editPassword)
      }

      this.axios
        .post(
          this.$model.wwwroot +
          "/procollege/manage_groups/admission/createApplicant/downloadDocumentScan",
          requestData,
          { responseType: 'blob' }
        )
        .then((response) => {
          const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = blobUrl
          link.download = scan.filename || 'scan'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(blobUrl)
        })
        .catch(() => {
          this.uploadError = 'Не удалось скачать файл'
        })
        .finally(() => {
          this.downloadingScanId = null
        })
    }
  }
}
</script>

<style>
.statement-documents-table {
  overflow: hidden;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
}

.statement-documents-table__row {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.statement-documents-table__row:hover {
  background: #f8fafc;
}

.statement-documents-table__row--selected {
  background: #ecfdf5;
}

.statement-documents-table__row--selected:hover {
  background: #dcfce7;
}

.statement-documents-table__mark {
  width: 48px;
  text-align: center;
}

.statement-documents-table.v-table>.v-table__wrapper>table>tbody>tr>td,
.statement-documents-table.v-table>.v-table__wrapper>table>thead>tr>th {
  height: auto;
}

.statement-documents-table.v-table>.v-table__wrapper>table>tbody>tr>td {
  padding-top: 4px;
  padding-bottom: 4px;
}

.statement-documents-table.v-table>.v-table__wrapper>table>tbody>tr>td.statement-documents-table__document {
  width: 100%;
  padding-top: 4px;
  padding-bottom: 4px;
}

.statement-documents-table__name {
  line-height: 1.35;
}

.statement-documents-table__comment {
  margin-top: 4px;
  background-color: #ffffff;
  border-radius: 4px;
}

.statement-documents-table__comment .v-field {
  background-color: #ffffff;
  min-height: 34px;
}

.statement-documents-table__comment .v-field__overlay {
  background-color: #ffffff;
}

.statement-documents-table__comment .v-field__input {
  min-height: 34px;
  padding-top: 4px;
  padding-bottom: 4px;
}

.statement-documents-table__receipt {
  margin-top: 12px;
}

.statement-document-scans {
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #e6e8ee;
}

.statement-document-scans__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.statement-document-scans__hint {
  margin: 4px 0 12px;
  color: #667085;
  line-height: 1.4;
}

.statement-document-scans__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.statement-document-scans__item {
  min-width: 0;
  max-width: 100%;
}

.statement-document-scans__upload-button {
  max-width: 100%;
  height: auto;
  min-height: 36px;
  text-align: left;
}

.statement-document-scans__upload-button .v-btn__content {
  min-width: 0;
  line-height: 1.3;
  overflow-wrap: anywhere;
  white-space: normal;
}

.statement-document-scans__list {
  display: grid;
  gap: 8px;
  margin-top: 14px;
}

.statement-document-scans__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e6e8ee;
  border-radius: 6px;
}

.statement-document-scans__file {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.statement-document-scans__doctype {
  font-weight: 600;
  color: #1f2937;
}

.statement-document-scans__filename {
  overflow: hidden;
  color: #667085;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.statement-document-scans__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
}

.statement-document-scans__input {
  display: none;
}

.statement-document-scans__message,
.statement-document-scans__error {
  margin-top: 10px;
  font-size: 14px;
}

.statement-document-scans__message {
  color: #027a48;
}

.statement-document-scans__error {
  color: #b42318;
}

@media (max-width: 640px) {
  .statement-document-scans__item,
  .statement-document-scans__upload-button {
    width: 100%;
  }
}
</style>
